// Shared currency configuration for the Pricing page and ROI calculator.
// INR is the canonical base; all stored amounts are in INR and converted on display.

export const CURRENCIES = [
    { code: "INR", symbol: "₹", rate: 1, locale: "en-IN" },
    { code: "USD", symbol: "$", rate: 0.012, locale: "en-US" },
    { code: "EUR", symbol: "€", rate: 0.011, locale: "en-IE" },
];

// USD → EUR derivation rate. Used when a product's USD price is given but
// the user has selected EUR. Static — refreshable manually if FX shifts.
export const USD_TO_EUR = 0.92;

/**
 * Format an amount that is ALREADY in the target currency (no conversion).
 * Used when explicit per-currency prices are configured (e.g., Mativex
 * is ₹12,000 / $149, not a math conversion of one to the other).
 *
 * @param {number} amount  amount already in target currency
 * @param {string} code    "INR" | "USD" | "EUR"
 */
export const formatAmount = (amount, code = "INR") => {
    const c = getCurrency(code);
    if (amount === null || amount === undefined || Number.isNaN(amount)) {
        return `${c.symbol}—`;
    }
    let formatted;
    if (code === "INR") {
        formatted = Math.max(0, Math.round(amount)).toLocaleString(c.locale);
    } else if (amount < 10) {
        // Keep two decimals for tiny per-seat prices (e.g. $2.49)
        formatted = amount.toLocaleString(c.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } else {
        formatted = Math.max(0, Math.round(amount)).toLocaleString(c.locale);
    }
    return `${c.symbol}${formatted}`;
};

/**
 * Pick the right currency-specific amount from a price object that holds
 * { INR, USD } (and optionally EUR). EUR is derived from USD when missing.
 *
 * @param {{INR:number, USD:number, EUR?:number}} priceByCcy
 * @param {string} code
 */
export const pickAmount = (priceByCcy, code = "INR") => {
    if (!priceByCcy) return null;
    if (priceByCcy[code] !== undefined && priceByCcy[code] !== null) {
        return priceByCcy[code];
    }
    if (code === "EUR" && typeof priceByCcy.USD === "number") {
        return priceByCcy.USD * USD_TO_EUR;
    }
    return priceByCcy.INR ?? priceByCcy.USD ?? null;
};

export const getCurrency = (code) =>
    CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

/** Format an INR amount in the selected currency, rounded for display. */
export const formatPrice = (amountInr, code = "INR") => {
    const c = getCurrency(code);
    const value = amountInr * c.rate;
    // INR (and any rate >= 1) — clean integer display.
    // For small currencies (USD/EUR), keep two decimals when the converted
    // value is under 10 (per-seat pricing rounds awkwardly otherwise — e.g.
    // ₹49/employee should NOT round to $0).
    let formatted;
    if (c.rate >= 1) {
        formatted = Math.max(0, Math.round(value)).toLocaleString(c.locale);
    } else if (value < 10) {
        formatted = value.toLocaleString(c.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } else {
        formatted = Math.max(0, Math.round(value)).toLocaleString(c.locale);
    }
    return `${c.symbol}${formatted}`;
};

/**
 * Best-effort, instant currency detection from the browser locale.
 * Falls through to USD for unknown locales (most globally legible default).
 * No network calls, no PII — safe to run on first paint.
 */
export const detectCurrencyFromLocale = () => {
    if (typeof navigator === "undefined") return "INR";
    const lang = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (!lang) return "USD";

    // India and Indian-language locales
    if (/^(en-in|hi|ta|te|bn|mr|gu|pa|kn|ml|or|as)\b/.test(lang)) return "INR";

    // EU / EEA / UK locales — UK has no dedicated GBP option here, so default to EUR
    if (
        /^(de|fr|it|es|nl|pt|pl|sv|da|fi|no|el|cs|ro|hu|sk|sl|hr|bg|et|lv|lt|mt|ga|is|en-gb|en-ie|en-mt|en-au)\b/.test(
            lang
        )
    ) {
        return "EUR";
    }

    // Default: USD covers en-US, en-CA, es-MX, pt-BR, ja, ko, zh, and the long tail
    return "USD";
};

/** Optional async refinement via our backend proxy (avoids browser CORS errors). Best-effort, fail-silent. */
export const detectCurrencyFromIp = async (signal) => {
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${base}/api/geo`, {
            signal,
            headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;
        const data = await res.json();
        // Backend already maps country → currency; just trust it.
        if (data && data.currency) return data.currency;
        return null;
    } catch {
        return null;
    }
};

